import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import CaptionedValue from '@/common/components/CaptionedValue/CaptionedValue'
import { useNumberFormatter } from '@/common/hooks'
import {
    getSizeCaptionKey,
    getThicknessDisplay,
    hasWebThickness,
    PROFILE_TYPES,
} from '@/common/utils/SheetUtils/SheetUtils'

const RenderDimensions = ({ quoteItemId, selectedPaths }) => {
    const { t } = useTranslation()
    const params = useParams()
    const { quoteId } = params
    const organisationId = useSelector(selectOrganisationId)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const drawingCleanerDecimalPlaces = (quoteItem) => (quoteItem?.isImperial ? 4 : 2)

    const { n } = useNumberFormatter({
        numberOfDecimalPlaces: drawingCleanerDecimalPlaces(quoteItem),
    })

    const pathDimensions = Object.values(selectedPaths).length === 1 ? Object.values(selectedPaths)[0] : null

    if (!quoteItem || !selectedPaths) return null

    const dimensions = quoteItem?.profile
        ? quoteItem.profile.toLowerCase() === 'circle'
            ? `${n(quoteItem.diameter)} x ${n(quoteItem.rotaryPartLength)}`
            : `${n(quoteItem.rotaryProfileWidth)} x ${n(quoteItem.rotaryProfileLength)} x ${n(
                  quoteItem.rotaryPartLength
              )}`
        : `${n(quoteItem.displayBoundBoxWidth)} x ${n(quoteItem.displayBoundBoxHeight)}`

    // QM-3695 (AC: "Circle, flat sheet, Angle and U-channel rendering are unchanged"): only an
    // IBeam has a second thickness term (web vs flange) to distinguish two otherwise-identical
    // records, so the suffix is scoped to that profile — everyone else keeps the plain
    // dimension string. Not shown for a selected-path measurement, which describes an arbitrary
    // region of the drawing rather than the part's own material.
    const thicknessDisplay = quoteItem?.profile === PROFILE_TYPES.I_BEAM ? getThicknessDisplay(quoteItem, n) : ''

    // The caption names the order of the part's own size. A selected-path measurement is an
    // arbitrary width x height on the drawing, so it stays uncaptioned.
    const sizeCaption = t(
        getSizeCaptionKey({
            hasLength: Boolean(quoteItem?.profile),
            isCircle: quoteItem?.profile?.toLowerCase() === 'circle',
        })
    )
    const caption = pathDimensions
        ? ''
        : hasWebThickness(quoteItem)
          ? `${sizeCaption} · ${t('web / flange')}`
          : sizeCaption

    return (
        <CaptionedValue caption={caption}>
            <Typography
                sx={quoteItem?.profile ? { whiteSpace: 'nowrap' } : undefined}
                variant="body1"
            >
                {pathDimensions
                    ? `${n(pathDimensions.width)} x ${n(pathDimensions.height)}`
                    : thicknessDisplay
                      ? `${dimensions} · ${thicknessDisplay}`
                      : dimensions}
            </Typography>
        </CaptionedValue>
    )
}

RenderDimensions.propTypes = {
    quoteItemId: PropTypes.string,
    selectedPaths: PropTypes.object,
}

export default RenderDimensions
